import { CronExpressionParser } from 'cron-parser';

// Category colors - matches AutomationsDashboard
// Each category has a main color and a light background variant
export const CATEGORY_COLORS = {
  sales: { main: '#1976d2', bg: '#e3f2fd' },
  labor: { main: '#7b1fa2', bg: '#f3e5f5' },
  tips: { main: '#2e7d32', bg: '#e8f5e9' },
  cogs: { main: '#e65100', bg: '#fff3e0' },
  other: { main: '#0288d1', bg: '#e1f5fe' },
};

// Status colors for calendar events
export const STATUS_COLORS = {
  scheduled: { bg: '#e3f2fd', border: '#1976d2', dot: '#1976d2', label: 'Scheduled' },
  success: { bg: '#e8f5e9', border: '#2e7d32', dot: '#2e7d32', label: 'Success' },
  failed: { bg: '#ffebee', border: '#c62828', dot: '#c62828', label: 'Failed' },
  cancelled: { bg: '#fff3e0', border: '#e65100', dot: '#e65100', label: 'Cancelled' },
};

/**
 * Clean a cron expression by stripping Snowflake timezone suffixes.
 * Extracted from AutomationsDashboard getNextRun() for shared use.
 */
export const cleanCronExpression = (cronExpr) => {
  if (!cronExpr) return null;
  let cleanCron = cronExpr.trim();
  const parts = cleanCron.split(/\s+/);
  if (parts.length > 6) {
    cleanCron = parts.slice(0, 6).join(' ');
  } else if (parts.length === 6 && /^[A-Za-z]/.test(parts[5])) {
    cleanCron = parts.slice(0, 5).join(' ');
  }
  return cleanCron;
};

/**
 * Generate scheduled (future) events from automation cron expressions.
 * Iterates each automation's cron within [rangeStart, rangeEnd] to produce calendar events.
 */
export const generateScheduledEvents = (automations, rangeStart, rangeEnd) => {
  const events = [];

  automations.forEach((automation) => {
    const cleanCron = cleanCronExpression(automation.scheduleCron);
    if (!cleanCron) return;

    try {
      const interval = CronExpressionParser.parse(cleanCron, {
        tz: automation.scheduleTimezone || 'America/Los_Angeles',
        currentDate: rangeStart,
        endDate: rangeEnd,
      });

      let hasNext = true;
      while (hasNext) {
        try {
          const next = interval.next().toDate();
          if (next > rangeEnd) break;

          events.push({
            id: `scheduled-${automation.id}-${next.getTime()}`,
            title: automation.name,
            start: next,
            end: new Date(next.getTime() + 30 * 60 * 1000), // 30 min duration
            automationId: automation.id,
            automation,
            status: 'scheduled',
            type: 'scheduled',
            categoryKey: automation.categoryKey || 'other',
          });
        } catch {
          hasNext = false;
        }
      }
    } catch (err) {
      console.warn('Failed to parse cron for calendar:', automation.name, err.message);
    }
  });

  return events;
};

/**
 * Transform server run-history records into calendar event objects.
 * automationsMap: { [automationId]: automation }
 */
export const mapHistoryToEvents = (historyRecords, automationsMap) => {
  return historyRecords.map((record) => {
    const automation = automationsMap[record.automationId] || {};
    const start = new Date(record.completedTime);

    return {
      id: `history-${record.automationId}-${start.getTime()}`,
      title: automation.name || record.taskName,
      start,
      end: new Date(start.getTime() + 30 * 60 * 1000),
      automationId: record.automationId,
      automation,
      status: record.status, // 'success', 'failed', 'cancelled'
      type: 'history',
      categoryKey: automation.categoryKey || 'other',
      errorMessage: record.errorMessage || null,
    };
  });
};

/**
 * Strip warehouse/schema prefix from automation names.
 * "THE_WAREHOUSE.TOAST_POS.HOURLY_EXPORT_GL_REFRESH" -> "Hourly Export GL Refresh"
 */
export const shortenAutomationName = (name) => {
  if (!name) return '';
  // Take the last segment after dots (if qualified name)
  const segments = name.split('.');
  let short = segments[segments.length - 1];
  // Convert UPPER_SNAKE_CASE to Title Case
  if (short === short.toUpperCase() && short.includes('_')) {
    short = short
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  return short;
};

/**
 * Collapse events from the same automation on the same day when they exceed a threshold.
 * Returns a new array where high-frequency events are merged into a single summary event.
 */
export const collapseFrequentEvents = (events, threshold = 3) => {
  // Group by automationId + date string
  const groups = {};
  events.forEach((e) => {
    const dateKey = `${e.automationId}-${e.start.getFullYear()}-${e.start.getMonth()}-${e.start.getDate()}`;
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(e);
  });

  const result = [];
  Object.values(groups).forEach((group) => {
    if (group.length <= threshold) {
      result.push(...group);
      return;
    }

    // Sort by start time
    group.sort((a, b) => a.start - b.start);
    const first = group[0];
    const last = group[group.length - 1];

    // Check if any events in this group failed
    const hasFailure = group.some((e) => e.status === 'failed');
    const allSuccess = group.every((e) => e.status === 'success');

    // Determine a summary status
    let summaryStatus = first.status; // default to scheduled
    if (hasFailure) summaryStatus = 'failed';
    else if (allSuccess) summaryStatus = 'success';

    result.push({
      ...first,
      id: `collapsed-${first.automationId}-${first.start.getTime()}`,
      title: first.title,
      start: first.start,
      end: new Date(last.end.getTime()),
      status: summaryStatus,
      collapsed: true,
      collapsedCount: group.length,
      collapsedEvents: group,
    });
  });

  return result;
};

/**
 * Returns style object for a calendar event based on category.
 * Background and left border both use category colors.
 */
export const getEventStyle = (event) => {
  const categoryColor = CATEGORY_COLORS[event.categoryKey] || CATEGORY_COLORS.other;

  return {
    style: {
      backgroundColor: categoryColor.bg,
      borderLeft: `3px solid ${categoryColor.main}`,
      borderTop: 'none',
      borderRight: 'none',
      borderBottom: 'none',
      borderRadius: 4,
      color: '#333',
      fontSize: 12,
      padding: '2px 6px',
    },
  };
};
